import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Building2, CreditCard, PhoneCall, RefreshCw, Server, Smartphone, UsersRound, UserRoundCog } from 'lucide-react';
import type { AdminSupportOverviewResponse, BillingStatus } from '@oracord/shared';
import { MetricCard, PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';
import { apiFetch, ApiError } from '../lib/api';

export default function Support() {
  const [data, setData] = useState<AdminSupportOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch<AdminSupportOverviewResponse>('/api/admin/support-overview');
      setData(response);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message || 'Failed to load support view');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return <SupportShell loading onRefresh={load} />;
  }

  if (error && !data) {
    return (
      <SupportShell onRefresh={load}>
        <SectionCard className="p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-red-500">Support overview unavailable</div>
              <p className="mt-2 text-sm leading-6 text-red-700">{error}</p>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                The read-only support summary is loaded from the server. Refresh to retry the same support-overview endpoint.
              </p>
            </div>
            <Server className="h-8 w-8 text-red-300" />
          </div>
        </SectionCard>
      </SupportShell>
    );
  }

  if (!data) {
    return (
      <SupportShell onRefresh={load}>
        <SectionCard className="p-5 text-sm text-slate-600">
          No support overview was returned for the active organization.
        </SectionCard>
      </SupportShell>
    );
  }

  const seatCap = data.billing.seats.cap;
  const seatUsage = `${data.billing.seats.used} / ${seatCap}`;
  const memberAndInviteCount = data.members.total + data.members.pendingInvitations;
  const unarchivedStaffCount = Math.max(data.staff.total - data.staff.archived, 0);

  return (
    <SupportShell organizationName={data.organization.name} loading={loading} onRefresh={load}>
      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <MetricCard
          icon={<CreditCard className="h-5 w-5 text-emerald-600" />}
          label="Billing"
          value={data.billing.active ? 'Active' : 'Inactive'}
          loading={loading && !data}
          tone={data.billing.active ? 'emerald' : 'amber'}
        />
        <MetricCard
          icon={<UsersRound className="h-5 w-5 text-blue-600" />}
          label="Seat usage"
          value={seatUsage}
          loading={loading && !data}
          tone="blue"
        />
        <MetricCard
          icon={<UsersRound className="h-5 w-5 text-purple-600" />}
          label="Members + invites"
          value={memberAndInviteCount}
          loading={loading && !data}
          tone="purple"
        />
        <MetricCard
          icon={<UserRoundCog className="h-5 w-5 text-slate-600" />}
          label="Staff profiles"
          value={data.staff.total}
          loading={loading && !data}
          tone="slate"
        />
        <MetricCard
          icon={<PhoneCall className="h-5 w-5 text-amber-600" />}
          label="Calls 30d"
          value={data.calls.last30d}
          loading={loading && !data}
          tone="amber"
        />
        <MetricCard
          icon={<Smartphone className="h-5 w-5 text-emerald-600" />}
          label="Lines / mobile access"
          value={`${data.numbers.total} / ${data.devices.active}`}
          loading={loading && !data}
          tone="emerald"
        />
      </div>

      <SectionCard className="p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Internal setup inspection summary</div>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
              This page is read-only and server-truth-backed for setup/support inspection. It summarizes the active organization, billing state,
              seats, access members, operational staff profiles, calls, business lines, and mobile access compatibility without turning Oracord into
              a manager comparison surface or daily workflow.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">Read-only</span>
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">Server summary</span>
          </div>
        </div>
      </SectionCard>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <SupportPanel
          eyebrow="Organization"
          title={data.organization.name}
          icon={<Building2 className="h-5 w-5 text-blue-600" />}
        >
          <Fact label="Name">{data.organization.name}</Fact>
          <Fact label="Slug">{data.organization.slug ?? '—'}</Fact>
          <Fact label="Created">{formatDateTime(data.organization.createdAt)}</Fact>
          <Fact label="Organization ID">{data.organization.id}</Fact>
        </SupportPanel>

        <SupportPanel
          eyebrow="Billing"
          title="Subscription state"
          icon={<CreditCard className="h-5 w-5 text-emerald-600" />}
          aside={<StatusBadge tone={billingStatusTone(data.billing.status)}>{data.billing.status}</StatusBadge>}
        >
          <Fact label="Plan">{data.billing.plan}</Fact>
          <Fact label="Active">{data.billing.active ? 'Yes' : 'No'}</Fact>
          <Fact label="Stripe customer">{data.billing.hasCustomer ? 'Connected' : 'Not yet'}</Fact>
          <Fact label="Trial end">{formatDate(data.billing.trialEnd)}</Fact>
          <Fact label="Current period end">{formatDate(data.billing.currentPeriodEnd)}</Fact>
          <Fact label="Cancel at period end">{data.billing.cancelAtPeriodEnd ? 'Yes' : 'No'}</Fact>
        </SupportPanel>

        <SupportPanel
          eyebrow="Seats"
          title="Seat usage"
          icon={<UsersRound className="h-5 w-5 text-purple-600" />}
        >
          <Fact label="Used / cap">{seatUsage}</Fact>
          <Fact label="Members">{data.billing.seats.members}</Fact>
          <Fact label="Pending invites">{data.billing.seats.pending}</Fact>
          <Fact label="Seat source">Billing summary</Fact>
        </SupportPanel>

        <SupportPanel
          eyebrow="Access"
          title="Members & invites"
          icon={<UsersRound className="h-5 w-5 text-blue-600" />}
          aside={<StatusBadge tone={data.members.pendingInvitations > 0 ? 'amber' : 'slate'}>{data.members.pendingInvitations} pending</StatusBadge>}
        >
          <Fact label="Total members">{data.members.total}</Fact>
          <Fact label="Owners">{data.members.owners}</Fact>
          <Fact label="Admins">{data.members.admins}</Fact>
          <Fact label="Members">{data.members.members}</Fact>
          <Fact label="Pending invitations">{data.members.pendingInvitations}</Fact>
        </SupportPanel>

        <SupportPanel
          eyebrow="Operations"
          title="Staff profiles"
          icon={<UserRoundCog className="h-5 w-5 text-slate-600" />}
        >
          <Fact label="Total profiles">{data.staff.total}</Fact>
          <Fact label="Unarchived profiles">{unarchivedStaffCount}</Fact>
          <Fact label="Linked to users">{data.staff.linkedToUser}</Fact>
          <Fact label="Archived">{data.staff.archived}</Fact>
        </SupportPanel>

        <SupportPanel
          eyebrow="Communication record"
          title="Call volume"
          icon={<PhoneCall className="h-5 w-5 text-amber-600" />}
        >
          <Fact label="Total calls">{data.calls.total}</Fact>
          <Fact label="Last 24 hours">{data.calls.last24h}</Fact>
          <Fact label="Last 7 days">{data.calls.last7d}</Fact>
          <Fact label="Last 30 days">{data.calls.last30d}</Fact>
        </SupportPanel>

        <SupportPanel
          eyebrow="Telephony"
          title="Business lines & mobile access"
          icon={<Smartphone className="h-5 w-5 text-emerald-600" />}
          className="xl:col-span-3"
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Fact label="Numbers mapped">{data.numbers.total}</Fact>
            <Fact label="Linked number profiles">{data.numbers.linkedProfiles}</Fact>
            <Fact label="Previous number mappings">{data.numbers.legacyMappings}</Fact>
            <Fact label="Mobile access records">{data.devices.total}</Fact>
            <Fact label="Active mobile access">{data.devices.active}</Fact>
            <Fact label="Revoked mobile access">{data.devices.revoked}</Fact>
          </div>
        </SupportPanel>
      </div>
    </SupportShell>
  );
}

function SupportShell({
  children,
  organizationName,
  loading = false,
  onRefresh,
}: {
  children?: ReactNode;
  organizationName?: string;
  loading?: boolean;
  onRefresh: () => void | Promise<void>;
}) {
  return (
    <div className="max-w-6xl space-y-5">
      <PageHeader
        eyebrow="Support"
        title="Customer setup inspection"
        description={(
          <>
            Read-only setup/support summary{organizationName ? <> for <strong>{organizationName}</strong></> : null}.
            Every card is backed by the existing support-overview response.
          </>
        )}
        action={(
          <button
            type="button"
            onClick={() => void onRefresh()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm shadow-slate-200/70 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      />

      {loading && !children ? <LoadingSupportCards /> : children}
    </div>
  );
}

function LoadingSupportCards() {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        {['Billing', 'Seat usage', 'Members', 'Staff', 'Calls', 'Engine'].map(label => (
          <MetricCard key={label} label={label} value="—" loading tone="slate" />
        ))}
      </div>
      <SectionCard className="p-5">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Loading support overview</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Fetching the read-only organization summary from the server.
        </p>
      </SectionCard>
    </>
  );
}

function SupportPanel({
  eyebrow,
  title,
  icon,
  aside,
  children,
  className = '',
}: {
  eyebrow: string;
  title: string;
  icon: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <SectionCard className={`overflow-hidden p-0 ${className}`.trim()}>
      <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50">{icon}</div>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</div>
            <h2 className="mt-1 truncate text-base font-semibold text-slate-950">{title}</h2>
          </div>
        </div>
        {aside}
      </header>
      <div className="space-y-3 bg-slate-50/50 p-4">{children}</div>
    </SectionCard>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm shadow-slate-200/50">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 break-words text-sm font-medium text-slate-900">{children}</div>
    </div>
  );
}

function billingStatusTone(status: BillingStatus) {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'emerald';
    case 'past_due':
    case 'incomplete':
    case 'paused':
      return 'amber';
    case 'canceled':
    case 'incomplete_expired':
    case 'unpaid':
      return 'rose';
    case 'none':
    default:
      return 'slate';
  }
}

function formatDate(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}
